(() => {
    "use strict";

    const MOBILE_QUERY = window.matchMedia("(max-width: 1080px)");
    const REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)");
    const IDLE_MS = 30000;
    const ACTIVE_RATIO = 0.52;
    const DESKTOP_ACTIVE_RATIO = 0.35;

    function getActiveRatio() {
        return usesMobileFolds() ? ACTIVE_RATIO : DESKTOP_ACTIVE_RATIO;
    }

    let activeFoldId = null;
    let idleTimer = null;
    let initialized = false;
    let lastArtHintsDetail = null;
    const interacted = new Set();
    const foldMap = new Map();
    const visibility = new Map();
    let observer = null;

    function usesMobileFolds() {
        return MOBILE_QUERY.matches;
    }

    function isEnabled() {
        return document.body.classList.contains("page-sobre-site")
            && document.body.classList.contains("has-section-snap")
            && !REDUCED_MOTION.matches;
    }

    function cleanupHintLayers() {
        document.querySelectorAll(".sobre-art-hint-layer").forEach((layer) => {
            layer.remove();
        });

        document.querySelectorAll(".sobre-interactive-target").forEach((el) => {
            el.classList.remove(
                "sobre-interactive-target",
                "is-hint-pending",
                "is-hint-pulse"
            );
            delete el.dataset.sobreHintId;
        });
    }

    function tagTarget(el, id) {
        if (!el || !id) {
            return null;
        }

        el.classList.add("sobre-interactive-target");
        el.dataset.sobreHintId = id;
        return { el, id };
    }

    function hintElements(target) {
        return [target?.el, target?.glowEl].filter(Boolean);
    }

    function setHintState(target, on, pulse = false) {
        if (!target?.el || interacted.has(target.id)) {
            return;
        }

        hintElements(target).forEach((el) => {
            el.classList.toggle("is-hint-pending", on);
        });

        if (!on) {
            hintElements(target).forEach((el) => {
                el.classList.remove("is-hint-pulse");
            });
            return;
        }

        if (pulse) {
            hintElements(target).forEach((el) => {
                el.classList.remove("is-hint-pulse");
            });
            void target.el.offsetWidth;
            hintElements(target).forEach((el) => {
                el.classList.add("is-hint-pulse");
            });
            return;
        }

        hintElements(target).forEach((el) => {
            el.classList.remove("is-hint-pulse");
        });
    }

    function refreshFoldHints(foldId, pulse = false) {
        const fold = foldMap.get(foldId);
        if (!fold || activeFoldId !== foldId) {
            return;
        }

        fold.targets.forEach((target) => {
            setHintState(target, !interacted.has(target.id), pulse);
        });

        fold.onRefresh?.();
    }

    function clearIdleTimer() {
        if (idleTimer !== null) {
            window.clearTimeout(idleTimer);
            idleTimer = null;
        }
    }

    function scheduleIdleTimer() {
        clearIdleTimer();
        if (!activeFoldId) {
            return;
        }

        idleTimer = window.setTimeout(() => {
            refreshFoldHints(activeFoldId, true);
            scheduleIdleTimer();
        }, IDLE_MS);
    }

    function setActiveFold(foldId) {
        if (activeFoldId === foldId) {
            return;
        }

        if (activeFoldId) {
            const previous = foldMap.get(activeFoldId);
            previous?.el?.classList.remove("is-sobre-fold-active");
            previous?.targets.forEach((target) => {
                hintElements(target).forEach((el) => {
                    el.classList.remove("is-hint-pending", "is-hint-pulse");
                });
            });
        }

        activeFoldId = foldId;
        clearIdleTimer();

        if (!foldId) {
            return;
        }

        const fold = foldMap.get(foldId);
        if (!fold) {
            return;
        }

        fold.el.classList.add("is-sobre-fold-active");
        refreshFoldHints(foldId, false);
        scheduleIdleTimer();
    }

    function markInteracted(id) {
        if (!id || interacted.has(id)) {
            return;
        }

        interacted.add(id);

        foldMap.forEach((fold) => {
            fold.targets.forEach((target) => {
                if (target.id === id) {
                    hintElements(target).forEach((el) => {
                        el.classList.remove("is-hint-pending", "is-hint-pulse");
                    });
                }
            });
        });
    }

    function bindTargetInteractions(targets) {
        targets.forEach((target) => {
            const handler = () => {
                markInteracted(target.id);
                scheduleIdleTimer();
            };

            target.el.addEventListener("click", handler);
            target.el.addEventListener("keydown", (event) => {
                if (event.key === "Enter" || event.key === " ") {
                    handler();
                }
            });
        });
    }

    function registerFold(id, el, targets, options = {}) {
        if (!el) {
            return;
        }

        const entry = {
            id,
            el,
            targets,
            onRefresh: options.onRefresh || null,
        };

        foldMap.set(id, entry);
        bindTargetInteractions(targets);
        observer?.observe(el);
    }

    function registerStaticFolds() {
        const hero = document.querySelector(".hero-sobre-site");
        if (!hero) {
            return;
        }

        const heroRope = hero.querySelector(".hero-sobre-site-rope");
        const ropeTargets = [];

        if (heroRope) {
            heroRope.querySelectorAll("[data-rope-no]").forEach((item, index) => {
                const target = tagTarget(item, `hero-rope-${index + 1}`);
                if (target) {
                    ropeTargets.push(target);
                }
            });
        }

        if (usesMobileFolds()) {
            const intro = hero.querySelector(".hero-sobre-site-intro");
            if (intro) {
                registerFold("intro", intro, []);
            }

            if (heroRope) {
                registerFold("hero-rope", heroRope, ropeTargets);
            }

            const artFold = document.querySelector(".sobre-site-art-fold");
            if (artFold) {
                registerFold("art", artFold, []);
            }

            const panelFold = document.querySelector(".sobre-site-panel-fold");
            if (panelFold) {
                registerFold("panel", panelFold, []);
            }

            const equipe = document.querySelector("#sobre-equipe");
            if (equipe) {
                registerFold("equipe", equipe, []);
            }

            return;
        }

        registerFold("hero-rope", hero, ropeTargets);

        const sobreSite = document.querySelector(".sobre-site");
        if (sobreSite) {
            registerFold("art", sobreSite, []);
        }

        const equipe = document.querySelector("#sobre-equipe");
        if (equipe) {
            registerFold("equipe", equipe, []);
        }
    }

    function registerArtHotspots(detail) {
        const {
            foldEl,
            birdImg,
            birdHit,
            ropeImg,
            ropeStage,
            featherMasks,
            tetherMask,
            bodyMask,
            birdImageData,
            ropeImageData,
            ropeMask,
            ropeBounds,
            canvasWidth,
            canvasHeight,
            ropeCanvasWidth,
            ropeCanvasHeight,
            cordaNos,
            onSpotActivate,
        } = detail;

        if (!foldEl || !isEnabled()) {
            return;
        }

        const fold = foldMap.get("art");
        if (!fold) {
            return;
        }

        const artTargets = fold.targets.filter((target) => !target.id.startsWith("art-"));
        const existingIds = new Set(artTargets.map((target) => target.id));

        function boundsFromMask(mask, width, height) {
            let minX = width;
            let minY = height;
            let maxX = 0;
            let maxY = 0;
            let found = false;

            for (let y = 0; y < height; y += 1) {
                for (let x = 0; x < width; x += 1) {
                    if (!mask[y * width + x]) {
                        continue;
                    }

                    found = true;
                    minX = Math.min(minX, x);
                    minY = Math.min(minY, y);
                    maxX = Math.max(maxX, x);
                    maxY = Math.max(maxY, y);
                }
            }

            if (!found) {
                return null;
            }

            return { minX, minY, maxX, maxY };
        }

        function createSpotLayer(parent, layerClass, options = {}) {
            const { prepend = false } = options;
            let layer = parent.querySelector(`.${layerClass}`);

            if (!layer) {
                layer = document.createElement("div");
                layer.className = `sobre-art-hint-layer ${layerClass}`;
                layer.setAttribute("aria-hidden", "true");

                if (prepend) {
                    parent.insertBefore(layer, parent.firstChild);
                } else {
                    parent.appendChild(layer);
                }
            }

            layer.replaceChildren();
            return layer;
        }

        function positionSpot(spot, img, bounds, sourceWidth, sourceHeight, parentEl) {
            if (!img || !bounds || !parentEl) {
                return;
            }

            const rendered = window.__sobreGetRenderedImageRect?.(img);
            const parentRect = parentEl.getBoundingClientRect();
            if (!rendered || parentRect.width <= 0 || parentRect.height <= 0) {
                return;
            }

            const scaleX = rendered.width / sourceWidth;
            const scaleY = rendered.height / sourceHeight;
            const left = (rendered.left - parentRect.left) + bounds.minX * scaleX;
            const top = (rendered.top - parentRect.top) + bounds.minY * scaleY;
            const width = Math.max(12, (bounds.maxX - bounds.minX) * scaleX);
            const height = Math.max(12, (bounds.maxY - bounds.minY) * scaleY);

            spot.style.left = `${left}px`;
            spot.style.top = `${top}px`;
            spot.style.width = `${width}px`;
            spot.style.height = `${height}px`;
        }

        function maskInBounds(mask, width, height, bounds) {
            const out = new Uint8Array(width * height);
            for (let y = bounds.minY; y <= bounds.maxY; y += 1) {
                for (let x = bounds.minX; x <= bounds.maxX; x += 1) {
                    const idx = y * width + x;
                    if (mask[idx]) {
                        out[idx] = 1;
                    }
                }
            }
            return out;
        }

        function buildSilhouetteCanvas(imageData, mask, sourceWidth, bounds) {
            const bw = bounds.maxX - bounds.minX + 1;
            const bh = bounds.maxY - bounds.minY + 1;
            const canvas = document.createElement("canvas");
            canvas.width = bw;
            canvas.height = bh;
            canvas.className = "sobre-art-hint-contour-canvas";
            const ctx = canvas.getContext("2d");
            const out = ctx.createImageData(bw, bh);
            const src = imageData.data;
            const goldR = 232;
            const goldG = 200;
            const goldB = 109;

            for (let y = bounds.minY; y <= bounds.maxY; y += 1) {
                for (let x = bounds.minX; x <= bounds.maxX; x += 1) {
                    const maskIdx = y * sourceWidth + x;
                    if (!mask[maskIdx]) {
                        continue;
                    }

                    const srcIdx = maskIdx * 4;
                    const alpha = src[srcIdx + 3];
                    if (alpha < 8) {
                        continue;
                    }

                    const outIdx = ((y - bounds.minY) * bw + (x - bounds.minX)) * 4;
                    out.data[outIdx] = goldR;
                    out.data[outIdx + 1] = goldG;
                    out.data[outIdx + 2] = goldB;
                    out.data[outIdx + 3] = alpha;
                }
            }

            ctx.putImageData(out, 0, 0);
            return canvas;
        }

        function addContourSpot(glowLayer, hitLayer, parentEl, img, imageData, mask, sourceWidth, sourceHeight, id, onActivate) {
            if (!mask || !imageData || existingIds.has(id)) {
                return null;
            }

            const bounds = boundsFromMask(mask, sourceWidth, sourceHeight);
            if (!bounds) {
                return null;
            }

            const glowWrap = document.createElement("span");
            glowWrap.className = "sobre-art-hint-contour-glow";

            const canvas = buildSilhouetteCanvas(imageData, mask, sourceWidth, bounds);
            glowWrap.appendChild(canvas);

            const hit = document.createElement("button");
            hit.type = "button";
            hit.className = "sobre-art-hint-hit sobre-interactive-target";
            hit.dataset.sobreHintId = id;
            hit.tabIndex = -1;
            hit.setAttribute("aria-hidden", "true");

            const reposition = () => {
                positionSpot(glowWrap, img, bounds, sourceWidth, sourceHeight, parentEl);
                positionSpot(hit, img, bounds, sourceWidth, sourceHeight, parentEl);
            };
            reposition();

            hit.addEventListener("click", (event) => {
                const rect = glowWrap.getBoundingClientRect();
                if (rect.width <= 0 || rect.height <= 0) {
                    return;
                }

                const localX = Math.floor(((event.clientX - rect.left) / rect.width) * canvas.width);
                const localY = Math.floor(((event.clientY - rect.top) / rect.height) * canvas.height);
                if (localX < 0 || localY < 0 || localX >= canvas.width || localY >= canvas.height) {
                    return;
                }

                const alpha = canvas.getContext("2d").getImageData(localX, localY, 1, 1).data[3];
                if (alpha < 24) {
                    return;
                }

                event.preventDefault();
                event.stopPropagation();
                markInteracted(id);
                onActivate?.(event, id);
                scheduleIdleTimer();
            });

            glowLayer.appendChild(glowWrap);
            hitLayer.appendChild(hit);

            const target = {
                el: hit,
                glowEl: glowWrap,
                id,
                reposition,
            };
            artTargets.push(target);
            existingIds.add(id);
            return target;
        }

        const activateSpot = (event, id) => {
            if (!event) {
                return;
            }

            onSpotActivate?.(event.clientX, event.clientY, id);
        };

        if (birdHit && birdImg && birdImageData && canvasWidth && canvasHeight) {
            const glowLayer = createSpotLayer(birdHit, "sobre-art-hint-layer--glow", { prepend: true });
            const hitLayer = createSpotLayer(birdHit, "sobre-art-hint-layer--hit");

            featherMasks.forEach((feather, index) => {
                addContourSpot(
                    glowLayer,
                    hitLayer,
                    birdHit,
                    birdImg,
                    birdImageData,
                    feather.mask,
                    canvasWidth,
                    canvasHeight,
                    `art-feather-${index + 1}`,
                    activateSpot
                );
            });

            if (tetherMask) {
                addContourSpot(
                    glowLayer,
                    hitLayer,
                    birdHit,
                    birdImg,
                    birdImageData,
                    tetherMask,
                    canvasWidth,
                    canvasHeight,
                    "art-tether",
                    activateSpot
                );
            }

            if (bodyMask) {
                addContourSpot(
                    glowLayer,
                    hitLayer,
                    birdHit,
                    birdImg,
                    birdImageData,
                    bodyMask,
                    canvasWidth,
                    canvasHeight,
                    "art-bird-body",
                    activateSpot
                );
            }
        }

        if (ropeStage && ropeImg && ropeImageData && ropeMask && ropeBounds && ropeCanvasWidth && ropeCanvasHeight) {
            const ropeGlowLayer = createSpotLayer(ropeStage, "sobre-art-hint-layer--glow", { prepend: true });
            const ropeHitLayer = createSpotLayer(ropeStage, "sobre-art-hint-layer--hit");
            const bandCount = Math.max(1, Array.isArray(cordaNos) ? cordaNos.length : 1);
            const spanY = Math.max(ropeBounds.maxY - ropeBounds.minY, 1);
            const bandHeight = spanY / bandCount;

            for (let index = 0; index < bandCount; index += 1) {
                const bandBounds = {
                    minX: ropeBounds.minX,
                    maxX: ropeBounds.maxX,
                    minY: Math.floor(ropeBounds.maxY - bandHeight * (index + 1)),
                    maxY: Math.floor(ropeBounds.maxY - bandHeight * index),
                };

                addContourSpot(
                    ropeGlowLayer,
                    ropeHitLayer,
                    ropeStage,
                    ropeImg,
                    ropeImageData,
                    maskInBounds(ropeMask, ropeCanvasWidth, ropeCanvasHeight, bandBounds),
                    ropeCanvasWidth,
                    ropeCanvasHeight,
                    `art-rope-${index + 1}`,
                    activateSpot
                );
            }
        }

        fold.targets = artTargets;

        fold.onRefresh = () => {
            artTargets.forEach((target) => {
                target.reposition?.();
            });
        };

        syncActiveFoldFromVisibility();

        if (activeFoldId === "art") {
            refreshFoldHints("art", false);
        }
    }

    function teardown() {
        clearIdleTimer();
        setActiveFold(null);
        observer?.disconnect();
        observer = null;
        cleanupHintLayers();
        foldMap.clear();
        visibility.clear();
        initialized = false;
    }

    function setupObserver() {
        if (observer) {
            observer.disconnect();
        }

        visibility.clear();

        observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                visibility.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
            });

            let bestId = null;
            let bestRatio = 0;

            visibility.forEach((ratio, element) => {
                if (ratio < getActiveRatio()) {
                    return;
                }

                const foldId = element.dataset.sobreFoldHints;
                if (!foldId || ratio <= bestRatio) {
                    return;
                }

                bestRatio = ratio;
                bestId = foldId;
            });

            setActiveFold(bestId);
        }, {
            threshold: [0.2, 0.35, 0.52, 0.68, 0.85],
        });

        foldMap.forEach((fold) => {
            fold.el.dataset.sobreFoldHints = fold.id;
            observer.observe(fold.el);
        });

        syncActiveFoldFromVisibility();
    }

    function syncActiveFoldFromVisibility() {
        let bestId = null;
        let bestRatio = 0;
        const minRatio = getActiveRatio();

        visibility.forEach((ratio, element) => {
            if (ratio < minRatio) {
                return;
            }

            const foldId = element.dataset.sobreFoldHints;
            if (!foldId || ratio <= bestRatio) {
                return;
            }

            bestRatio = ratio;
            bestId = foldId;
        });

        if (!bestId) {
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

            foldMap.forEach((fold) => {
                const rect = fold.el.getBoundingClientRect();
                if (rect.height <= 0) {
                    return;
                }

                const visible = Math.max(
                    0,
                    Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
                );
                const ratio = visible / Math.min(rect.height, viewportHeight);

                if (ratio < minRatio || ratio <= bestRatio) {
                    return;
                }

                bestRatio = ratio;
                bestId = fold.id;
            });
        }

        if (bestId) {
            setActiveFold(bestId);
        }
    }

    function boot() {
        if (!isEnabled()) {
            teardown();
            return;
        }

        if (initialized) {
            return;
        }

        initialized = true;
        registerStaticFolds();
        setupObserver();

        if (lastArtHintsDetail) {
            registerArtHotspots(lastArtHintsDetail);
        }

        syncActiveFoldFromVisibility();
    }

    document.addEventListener("sobre:art-hints-ready", (event) => {
        lastArtHintsDetail = event.detail || {};
        if (initialized) {
            registerArtHotspots(lastArtHintsDetail);
        }
    });

    document.addEventListener("sobre:interactive-used", (event) => {
        markInteracted(event.detail?.id || "");
        scheduleIdleTimer();
    });

    window.addEventListener("resize", () => {
        if (activeFoldId) {
            refreshFoldHints(activeFoldId, false);
        }
    }, { passive: true });

    MOBILE_QUERY.addEventListener("change", () => {
        teardown();
        boot();
    });

    REDUCED_MOTION.addEventListener("change", () => {
        teardown();
        boot();
    });

    window.siteReady(boot);
})();
